import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import dayjs from 'dayjs'

import { cn } from '@/app/utils'

import { monthNames } from '../../../monthpicker/ui/MonthPicker.constants'
import { PickerMode } from '../Calendar.types'

type Props = {
  month: Date
  setMonth: (month: Date) => void
  onMonthClick?: () => void
  fromMonth?: Date
  disabled?: {
    before?: Date
    after?: Date
  }
  pickerMode?: PickerMode
  disableMonthSwitch?: boolean
  isPrevMonthDisabled?: boolean
}

export const CalendarCaption = ({
  month,
  setMonth,
  onMonthClick,
  fromMonth,
  disabled,
  pickerMode,
  isPrevMonthDisabled = false,
  disableMonthSwitch = false
}: Props) => {
  const currentMonth = dayjs(month).startOf('month')
  const monthIndex = month.getMonth()
  const year = month.getFullYear()
  const minMonth = dayjs(disabled?.before || fromMonth).startOf('month')
  const maxMonth = disabled?.after
    ? dayjs(disabled.after).startOf('month')
    : undefined

  const isAtMaxMonthLimit = (limit: dayjs.Dayjs) =>
    pickerMode === 'months'
      ? year >= limit.year()
      : currentMonth.isSame(limit, 'month')

  const isNextDisabledByLimit = maxMonth ? isAtMaxMonthLimit(maxMonth) : false

  const isPrevDisabled =
    isPrevMonthDisabled ||
    (Boolean(disabled?.before || fromMonth) &&
      (pickerMode === 'months'
        ? year <= minMonth.year()
        : currentMonth.isSame(minMonth, 'month'))) ||
    disableMonthSwitch

  const isNextDisabled = isNextDisabledByLimit || disableMonthSwitch

  return (
    <div className="flex justify-between pb-12">
      <button
        aria-label="Предыдущий год"
        type="button"
        onClick={() =>
          setMonth(
            pickerMode === 'months'
              ? new Date(year - 1, monthIndex)
              : new Date(year, monthIndex - 1)
          )
        }
        disabled={isPrevDisabled}
      >
        <ChevronLeftIcon
          className={cn('h-24 w-24', isPrevDisabled && 'text-grey-30')}
        />
      </button>

      <div className="flex gap-4">
        {
          <button
            aria-label={
              pickerMode === 'months'
                ? 'Открыть выбор дня'
                : 'Открыть выбор месяца'
            }
            className="flex items-center gap-4 font-semibold capitalize text-16"
            type="button"
            onClick={onMonthClick}
          >
            {pickerMode === 'months' ? 'Месяц' : monthNames[monthIndex]}
            {Boolean(onMonthClick) && (
              <ChevronDownIcon
                className={cn(
                  'h-12 w-12',
                  pickerMode === 'months' && 'rotate-180'
                )}
              />
            )}
          </button>
        }
        <p className="flex items-center gap-4 font-semibold text-16">{year}</p>
      </div>

      <button
        aria-label="Следующий год"
        type="button"
        onClick={() =>
          setMonth(
            pickerMode === 'months'
              ? new Date(year + 1, monthIndex)
              : new Date(year, monthIndex + 1)
          )
        }
        disabled={isNextDisabled && Boolean(onMonthClick)}
      >
        <ChevronRightIcon
          className={cn(
            'h-24 w-24',
            isNextDisabled && Boolean(onMonthClick) && 'text-grey-30'
          )}
        />
      </button>
    </div>
  )
}
